import { useBusinessMetrics } from '../hooks/useBusinessMetrics';
import { useActiveBusinessUrls } from '../hooks/useActiveBusinessUrls';
import GeographicDistribution from './GeographicDistribution';

export default function BusinessStatsOverview() {
  const { metrics, loading, error } = useBusinessMetrics();
  const { businesses: activeBusinessUrls, loading: urlsLoading, error: urlsError } = useActiveBusinessUrls();

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 p-4">
        <p className="text-sm text-red-800">
          Failed to load business statistics: {error.message}
        </p>
      </div>
    );
  }

  const paidCount = metrics.starterPlanCount + metrics.steadyPlanCount + metrics.growthPlanCount;
  const activeRate = metrics.totalBusinesses > 0
    ? ((metrics.activeBusinesses / metrics.totalBusinesses) * 100).toFixed(1)
    : '0';
  const insuranceRate = metrics.totalBusinesses > 0
    ? ((metrics.businessesWithInsurance / metrics.totalBusinesses) * 100).toFixed(1)
    : '0';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Service Provider Statistics</h2>
        {loading && (
          <span className="text-sm text-gray-500">Updating...</span>
        )}
      </div>

      {/* Top-Level Metrics */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {/* Total Providers */}
        <div className="rounded-lg bg-white p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Providers</p>
              <p className="mt-2 text-3xl font-semibold text-gray-900">
                {loading ? '...' : metrics.totalBusinesses.toLocaleString()}
              </p>
            </div>
            <div className="rounded-full bg-blue-100 p-3">
              <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Active Providers */}
        <div className="rounded-lg bg-white p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Providers</p>
              <p className="mt-2 text-3xl font-semibold text-green-600">
                {loading ? '...' : metrics.activeBusinesses.toLocaleString()}
              </p>
              <p className="mt-1 text-xs text-gray-500">{activeRate}% of total</p>
            </div>
            <div className="rounded-full bg-green-100 p-3">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Total Jobs Completed */}
        <div className="rounded-lg bg-white p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Jobs Completed</p>
              <p className="mt-2 text-3xl font-semibold text-purple-600">
                {loading ? '...' : metrics.totalCompletedJobs.toLocaleString()}
              </p>
              <p className="mt-1 text-xs text-gray-500">across all providers</p>
            </div>
            <div className="rounded-full bg-purple-100 p-3">
              <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Breakdown */}
      <div className="rounded-lg bg-white p-6 shadow-sm border border-gray-200">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Subscription Breakdown</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {/* Free Plan */}
          <div className="border-l-4 border-gray-400 pl-4">
            <p className="text-sm font-medium text-gray-600">Free Plan</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">
              {loading ? '...' : metrics.freePlanCount.toLocaleString()}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              {metrics.totalBusinesses > 0
                ? `${((metrics.freePlanCount / metrics.totalBusinesses) * 100).toFixed(1)}%`
                : '0%'}
            </p>
          </div>

          {/* Starter Plan */}
          <div className="border-l-4 border-blue-500 pl-4">
            <p className="text-sm font-medium text-gray-600">Starter Plan</p>
            <p className="mt-1 text-2xl font-semibold text-blue-600">
              {loading ? '...' : metrics.starterPlanCount.toLocaleString()}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              {metrics.totalBusinesses > 0
                ? `${((metrics.starterPlanCount / metrics.totalBusinesses) * 100).toFixed(1)}%`
                : '0%'}
            </p>
          </div>

          {/* Steady Plan */}
          <div className="border-l-4 border-green-500 pl-4">
            <p className="text-sm font-medium text-gray-600">Steady Plan</p>
            <p className="mt-1 text-2xl font-semibold text-green-600">
              {loading ? '...' : metrics.steadyPlanCount.toLocaleString()}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              {metrics.totalBusinesses > 0
                ? `${((metrics.steadyPlanCount / metrics.totalBusinesses) * 100).toFixed(1)}%`
                : '0%'}
            </p>
          </div>

          {/* Growth Plan */}
          <div className="border-l-4 border-amber-500 pl-4">
            <p className="text-sm font-medium text-gray-600">Growth Plan</p>
            <p className="mt-1 text-2xl font-semibold text-amber-600">
              {loading ? '...' : metrics.growthPlanCount.toLocaleString()}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              {metrics.totalBusinesses > 0
                ? `${((metrics.growthPlanCount / metrics.totalBusinesses) * 100).toFixed(1)}%`
                : '0%'}
            </p>
          </div>

          {/* Total Paid */}
          <div className="border-l-4 border-indigo-500 pl-4">
            <p className="text-sm font-medium text-gray-600">Total Paid</p>
            <p className="mt-1 text-2xl font-semibold text-indigo-600">
              {loading ? '...' : paidCount.toLocaleString()}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              {metrics.totalBusinesses > 0
                ? `${((paidCount / metrics.totalBusinesses) * 100).toFixed(1)}%`
                : '0%'}
            </p>
          </div>
        </div>
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* Insurance Coverage */}
        <div className="rounded-lg bg-white p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">Insurance Coverage</p>
              <p className="mt-2 text-2xl font-semibold text-gray-900">
                {loading ? '...' : metrics.businessesWithInsurance.toLocaleString()}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                {insuranceRate}% have active insurance
              </p>
            </div>
            <div className="rounded-full bg-emerald-100 p-3">
              <svg className="h-6 w-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Inactive Providers */}
        <div className="rounded-lg bg-white p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600">Inactive Providers</p>
              <p className="mt-2 text-2xl font-semibold text-orange-600">
                {loading ? '...' : metrics.inactiveBusinesses.toLocaleString()}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                {metrics.totalBusinesses > 0
                  ? `${((metrics.inactiveBusinesses / metrics.totalBusinesses) * 100).toFixed(1)}%`
                  : '0%'} of total
              </p>
            </div>
            <div className="rounded-full bg-orange-100 p-3">
              <svg className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Geographic Distribution */}
      <GeographicDistribution 
        stateStats={metrics.businessesByState}
        cityStats={metrics.businessesByCity}
        loading={loading}
      />

      {/* Active Business URLs */}
      <div className="rounded-lg bg-white p-6 shadow-sm border border-gray-200">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Active Business Landing Pages</h3>
        {urlsError && (
          <div className="rounded-lg bg-red-50 p-4 mb-4">
            <p className="text-sm text-red-800">
              Failed to load active business URLs: {urlsError.message}
            </p>
          </div>
        )}
        {urlsLoading ? (
          <div className="text-center py-4">
            <p className="text-sm text-gray-500">Loading URLs...</p>
          </div>
        ) : activeBusinessUrls.length === 0 ? (
          <p className="text-sm text-gray-500">No active businesses with landing pages</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Business Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    URL
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {activeBusinessUrls.map((business) => (
                  <tr key={business.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {business.business_name}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <a
                        href={business.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 hover:underline break-all"
                      >
                        {business.url}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-4 text-xs text-gray-500">
              Showing {activeBusinessUrls.length} active business{activeBusinessUrls.length !== 1 ? 'es' : ''} with landing pages
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
